import { useRef } from 'react';
import { downloadMultipleFiles } from '@he-novation/front-shared/utils/downloadMultipleFiles';
import { atom, useSetAtom } from 'jotai';
import { cloneDeep } from 'lodash';

export type Download = {
    groupName: string;
    url: string;
    name: string;
    size: number;
    progress: number;
};

type DownloaderState = {
    downloads: Download[];
    pending: Download[];
    finished: Download[];
    errors: Download[];
};

export const downloaderAtom = atom<DownloaderState>({
    downloads: [],
    pending: [],
    finished: [],
    errors: []
});
const downloaderData: DownloaderState = {
    downloads: [],
    pending: [],
    finished: [],
    errors: []
};

export function useDownloader() {
    const _setState = useSetAtom(downloaderAtom);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const lastUpdate = useRef(0);

    const setState = (newState: DownloaderState) => {
        clearTimeout(timeoutRef.current);
        if (Date.now() - lastUpdate.current < 300) {
            timeoutRef.current = setTimeout(() => {
                setState(newState);
            }, 300);
        } else {
            lastUpdate.current = Date.now();
            const clonedState = cloneDeep(newState);
            _setState(clonedState);
        }
    };

    return {
        downloadMultipleFiles(
            groupName: string,
            urls: { url: string; name?: string | null; size: number }[]
        ) {
            urls.forEach((url) => {
                downloaderData.pending.push({
                    groupName,
                    url: url.url,
                    name: url.name || '',
                    size: url.size,
                    progress: 0
                });
            });

            setState(downloaderData);

            downloadMultipleFiles(urls, {
                onStart: (download) => {
                    downloaderData.pending = downloaderData.pending.filter(
                        (d) => d.url !== download.url
                    );
                    downloaderData.downloads.push({
                        groupName,
                        url: download.url,
                        name: download.name,
                        size: download.size,
                        progress: 0
                    });
                    setState(downloaderData);
                },
                onProgress: (download, progress) => {
                    const index = downloaderData.downloads.findIndex((d) => d.url === download.url);
                    if (index !== -1) {
                        downloaderData.downloads[index].progress = progress;
                        setState(downloaderData);
                    }
                },
                onComplete: (download) => {
                    const index = downloaderData.downloads.findIndex((d) => d.url === download.url);
                    if (index !== -1) {
                        downloaderData.finished.push(downloaderData.downloads[index]);
                        downloaderData.downloads.splice(index, 1);
                        setState(downloaderData);
                    }
                },
                onError(download) {
                    let sources = ['downloads', 'pending'];
                    let index = -1;
                    let sourceIndex = -1;
                    while (index === -1 && sourceIndex < sources.length) {
                        sourceIndex++;
                        const source = sources[sourceIndex];
                        index = downloaderData[source].findIndex((d) => d.url === download.url);
                    }
                    if (index !== -1) {
                        downloaderData.errors.push(downloaderData[sources[sourceIndex]][index]);
                        downloaderData[sources[sourceIndex]].splice(index, 1);
                        setState(downloaderData);
                    }
                }
            });
        }
    };
}
